import { useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { skipToken } from '@reduxjs/toolkit/query'
import { t, Trans } from '@lingui/macro'
import { Button, Stack, Typography } from '@mui/material'

import HeartbeatLoader from '@/components/ui/loaders/HeartbeatLoader'
import AccessDenied from '@/components/ui/template/AccessDenied'
import { useGetConsultationQuery } from '@/core/services/consultation'
import { useGetPatientQuery } from '@/core/services/patient'
import { EfficienceWebIframe } from '@/features/dmp-connect'
import { FeatureFlag, useIsFlagEnabled } from '@/hooks/useIsFlagEnabled'
import { selectDMPConnectTlsiConnectorId } from '@/redux/reducers/dmp-connect.reducer'
import { useAppSelector } from '@/redux/rootReducer'

import { useDmpFlow } from '../model/useDMPFlow'
import { useMssFlow } from '../model/useMssFlow'
import { useMessageEvent } from '../model/useMessageEvent'
import NotFoundPage from '@/pages/error/NotFoundPage'
import { Patient } from '@/core/models/patient.model'

export function EfficienceWebPage() {
  const [searchParams] = useSearchParams()
  const consultationId = searchParams.get('consultationId')
  const patientIdParam = searchParams.get('patientId')
  const mode = searchParams.get('mode')

  const isDmpConnectEnabled = useIsFlagEnabled(FeatureFlag.DmpConnect)
  const tlsiConnectorId = useAppSelector(selectDMPConnectTlsiConnectorId)

  const { data: consultation, isLoading: isLoadingConsultation, isError: isConsultationError } = useGetConsultationQuery(consultationId ?? skipToken)

  // Resolve patient ID: either from consultation or directly from URL param
  const resolvedPatientId = consultation?.patient._id ?? patientIdParam
  const { data: patient, isLoading: isLoadingPatient, isError: isPatientError } = useGetPatientQuery(resolvedPatientId ?? skipToken)

  if (!isDmpConnectEnabled || !tlsiConnectorId) {
    return (
      <div className="w-screen h-screen flex flex-col items-center justify-center">
        <AccessDenied title={t`DMP Connect`} description={t`DMP service is not available`}>
          <Button component={Link} to={'/consultations'} variant="contained">
            <Trans>Back</Trans>
          </Button>
        </AccessDenied>
      </div>
    )
  }

  const isLoading = isLoadingPatient || (consultationId ? isLoadingConsultation : false)
  const isError = isPatientError || (consultationId ? isConsultationError : false)

  if (isLoading) {
    return <PageLoader />
  }

  if (isError) {
    return <PageError />
  }

  if (!patient) return <NotFoundPage />

  return <EfficienceWeb patient={patient} mssClientMode={mode === 'mss'} />
}

function EfficienceWeb({ patient, mssClientMode }: { patient: Patient; mssClientMode: boolean }) {
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const [isLoggedIn, setIsLoggedIn] = useState(false)

  const { dmpStatus, setDmpStatus, testDmpReqId, openDmpReqId } = useDmpFlow({
    iframe: iframeRef.current,
    isLoggedIn,
    patientInsNIR: patient.insNIR,
    mssClientMode,
  })

  useMssFlow({
    iframe: iframeRef.current,
    isLoggedIn,
    patientInsNIR: patient.insNIR,
    patientLastname: patient.userInfo.surname,
    patientFirstname: patient.userInfo.name,
    mssClientMode,
  })

  const { onMessageEvent } = useMessageEvent({
    patientId: patient._id,
    dmpStatus,
    setDmpStatus,
    testDmpReqId,
    openDmpReqId,
  })

  return (
    <div className="h-screen">
      <EfficienceWebIframe
        mssClientMode={mssClientMode}
        onLogin={() => {
          setIsLoggedIn(true)
        }}
        onMessageEvent={onMessageEvent}
        ref={iframeRef}
      />
    </div>
  )
}

function PageLoader() {
  return (
    <Stack flex={1} alignItems="center" justifyContent="center" height="100vh">
      <HeartbeatLoader sx={{ transform: 'scale(4)' }} />
    </Stack>
  )
}

function PageError() {
  return (
    <Stack flex={1} alignItems="center" justifyContent="center" height="100vh" gap={2}>
      <Typography color="error">
        <Trans>Failed to load consultation or patient data</Trans>
      </Typography>
      <Button component={Link} to={'/consultations'} variant="contained">
        <Trans>Back</Trans>
      </Button>
    </Stack>
  )
}
